import { Trans, useTranslation } from 'react-i18next'

import { useTransComponents } from '@/composables/useTransComponents'

import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import UITitle from '@/components/UI/UITitle'

import SummarySwirl from '../SummarySwirl'

export type SummaryPartDescriptionProps = {
  leaving?: boolean
  title?: string
  introduction?: string
  subtitle1?: string
  text1?: string
  subtitle2?: string
  text2?: string
  cta?: {
    label: string
    url: string
  }
  background?: UIIconType
}

export default function SummaryPartDescription({
  leaving,
  title,
  introduction,
  subtitle1,
  text1,
  subtitle2,
  text2,
  cta,
  background = 'background1',
}: SummaryPartDescriptionProps) {
  const { t } = useTranslation()
  const { components } = useTransComponents()

  const texts = [
    { subtitle: subtitle1, text: text1 },
    { subtitle: subtitle2, text: text2 },
  ]

  return (
    <section
      className={`SummaryPartDescription flex flex-col w-full items-center justify-center ${leaving ? 'transition-opacity duration-500 ease-out-quad opacity-0' : '0'}`}
      style={
        {
          '--color-accent': 'var(--color-mid-blue)',
        } as React.CSSProperties
      }
    >
      <UITitle
        is='h2'
        className='text-heading-h1 text-center mt-120 z-2 w-full'
        hasScrollTrigger={false}
        i18nKey={title}
      />

      <div className={`relative w-full`}>
        <div
          aria-hidden='true'
          className='absolute -z-1 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'
        >
          <UIIcon icon={background} className='text-[1250px] text-secondary' />
        </div>

        <div className={`relative z-2 container py-40 xl:py-120`}>
          <p
            className='text-body-l text-center col-start-1 col-span-10 lg:col-start-3 lg:col-span-8 px-16 md:px-32'
            style={
              {
                '--color-accent': 'var(--color-mid-blue)',
                '--color-secondary': 'var(--color-peach)',
              } as React.CSSProperties
            }
          >
            <Trans components={components}>{introduction}</Trans>
          </p>
          <div className='relative col-start-1 col-span-12 md:col-start-2 md:col-span-8 lg:col-start-3 flex flex-col md:flex-row gap-24 md:gap-0 justify-between my-16 md:mt-24 lg:mt-48'>
            <UIIcon
              icon={background}
              className='text-16 md:text-32 text-scarlet md:absolute m-auto md:m-0 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            />
            {texts.map(({ text, subtitle }, index) => (
              <div key={index} className='flex w-full md:w-cols-3 justify-center'>
                <div className='flex flex-col gap-16 text-center md:text-left'>
                  <h3 className='text-20 md:text-24 font-semibold lg:mb-20'>
                    <Trans components={components}>{subtitle}</Trans>:
                  </h3>
                  <div className='text-body whitespace-pre-line text-left'>
                    <Trans components={components}>{text}</Trans>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SummarySwirl className='relative w-full col-start-1 col-span-12' />
    </section>
  )
}
